import DeleteIcon from '@mui/icons-material/Delete';
import SensorsIcon from '@mui/icons-material/Sensors';
import { Box } from '@mui/material';
import strings from '../Globalization';
import ActionIconButton from '../components/common/ActionIconButton';
import consts from '../consts';
import Model from '../models/model';
import util from '../util';
import connectionService from '../services/Connection';
import AssetCodeTable from './AssetCodeTable';


const AssetCodesCell = params => (
	<SensorsIcon fontSize="medium" />
)

const ImageCell = (params) => !!params.row.thumbnail && <Box component="img" sx={{ width: 36, height: 48 }} src={`${connectionService.Thumbnail}${params.row.thumbnail}`} />;

const model = new Model({
	name: 'Assets',
	api: 'Assets',
	idProperty: '_id',
	defaultSort: 'lastTransitionDate DESC',
	disableDelete: true,
	showGridRefreshBtn: true,
	gridRefreshInterval: 10000, //Long polling ms
	title: 'Assets',
	disableAdd: () => {
		const roles = util.localData?.loginData?.roles;
		const isReadOnly = Array.isArray(roles)
			? roles.includes('Administrator') || roles.includes('Viewer')
			: roles === 'Administrator' || roles === 'Viewer';
		return isReadOnly;
	},
	readOnly: () => {
		const roles = util.localData?.loginData?.roles;
		const isReadOnly = Array.isArray(roles)
			? roles.includes('Administrator') || roles.includes('Viewer')
			: roles === 'Administrator' || roles === 'Viewer';
		return isReadOnly;
	},
	formTitleField: 'assetName',
	customizeSaveData: (toSave) => {
		if (toSave.assetCategory === 'item') {
			delete toSave.userFirstName;
			delete toSave.userLastName;
		} else {
			delete toSave.serialNumber;
			delete toSave.maxHangTime;
		}
		return toSave;
	},
	initialValues: {
		activeYN: true,
		maxHangTime: 0
	},
	relations: [
		{
			relation: AssetCodeTable,
			column: 'codes',
			getParentFilters: ({ record, id }) => ({
				filter: [{ operator: '$eq', value: id || record?._id, property: 'assetId', type: 'numeric' }]
			}),
			getUrlSearchParams: ({ record, id, selectedId }) => {
				const urlSearchParams = new URLSearchParams();
				const recordId = record?._id || id || selectedId;
				urlSearchParams.set('assetId', recordId);
				urlSearchParams.set('returnTo', encodeURIComponent(`/asset?relation=codes&selected=${recordId}&assetId${recordId}`));
				return urlSearchParams;
			}
		}
	],
	comboTypes: [
		{ type: 'AssetCategory', loaded: true },
		{ type: 'CurrentLocation', loaded: true },
		{ type: 'Site', loaded: true }
	],
	formGroups: [
		{
			fields: [
				'assetName',
				'assetCategory',
				'assetType',
				'userFirstName',
				'userLastName',
				'serialNumber',
				'maxHangTime',
				'siteId',
				'locationId',
				'activeYN',
				'interfacedYN',
				'thumbnail'
			]
		}
	],
	columns: [
		{
			field: 'thumbnail',
			header: 'Thumbnail',
			label: 'Image',
			width: 70,
			type: 'file',
			accept: 'image/png',
			maxSize: 50000,
			renderCell: ImageCell,
			sortable: false,
			filterable: false,
			fileLocation: `${connectionService.Thumbnail}`
		},
		{ field: 'assetName', header: 'AssetName', width: 250, required: true },
		{
			field: 'serialNumber',
			header: 'SerialNumberSecond',
			width: 150,
			inputWhen: [['assetCategory', 'item']]
		},
		{
			field: 'assetCategory',
			header: 'AssetCategory',
			width: 150,
			type: 'string',
			lookup: 'AssetCategory',
			required: true
		},
		{ header: 'AssetType', field: 'assetType', width: 150, excludeOnGrid: true },

		{
			field: 'userFirstName',
			header: 'FirstName',
			width: 150,
			inputWhen: [['assetCategory', 'user']]
		},
		{
			field: 'userLastName',
			header: 'LastName',
			width: 150,
			inputWhen: [['assetCategory', 'user']]
		},

		{
			field: 'siteId',
			header: 'SiteId',
			width: 150,
			required: true,
			lookup: 'Site'
		},
		{
			field: 'locationId',
			header: 'CurrentLocation',
			lookup: 'CurrentLocation',
			isNotformfield: true,
			renderCell: (params) => {
				let locations = [];
				if (typeof params.row.locations === 'object') {
					locations = Object.values(params.row.locations);
				}
				return locations.join(', ');
			},
			width: 150
		},
		{ field: 'activeYN', header: 'Active', type: 'bool', width: 140 },
		{ field: 'interfacedYN', header: 'InterfacedYN', type: 'bool', width: 150, disabled: true },
		{
			field: 'locationId',
			header: 'CurrentLocation',
			lookup: 'CurrentLocation',
			preserveTypeOnSubmit: true,
			multiple: true,
			readOnly: true,
			renderCell: (params) => {
				const combos = params.colDef.getCombos();
				let locations = [];
				if (typeof params.formattedValue === 'string' && Array.isArray(combos?.CurrentLocation)) {
					locations = params.formattedValue.split(',').reduce((acc, curr) => {
						const index = combos.CurrentLocation.findIndex((item) => item.LookupId === curr);
						if (index > -1) {
							acc.push(combos.CurrentLocation[index].DisplayValue);
						}
						return acc;
					}, []);
				}
				return locations.join(', ');
			},
			autocompleteValueFormatter: (value) => {
				let formatedValue = '';
				if (Array.isArray(value)) {
					formatedValue = value.map((entry) => entry?.id || entry).join(',');
				}
				return formatedValue;
			},
			autocompleteValueParser: (value, options) => {
				let parsedValue = [];
				try {
					if (typeof value === 'string') {
						parsedValue = value.replace('[', '').replace(']', '').split(',');
					} else if (Array.isArray(value)) {
						parsedValue = value;
					}
				} catch (error) { /* empty */ }

				return parsedValue;
			},
			headerFilter: true,
			headerFilterProps: { label: 'Location', multiple: true },
			standaloneHeaderFilter: true,
			width: 300
		},

		{
			field: 'codes',
			header: 'Codes',
			width: 120,
			isNotformfield: true,
			renderCell: AssetCodesCell,
			sortable: false,
			filterable: false
		},
		{
			field: 'totalCodes',
			header: 'TotalCodes',
			width: 100,
			isNotformfield: true,
			sortable: false,
			filterable: false
		},
		{
			field: 'maxHangTime',
			header: 'HangTime',
			width: 200,
			minValue: 0,
			maxValue: 50,
			type: 'number',
			align: 'left',
			headerAlign: 'left',
			min: [['assetCategory', 'item']],
			max: [['assetCategory', 'item']],
			inputWhen: [['assetCategory', 'item']]
		},

		{
			field: 'lastTransitionDate',
			header: 'LastSeenDateAndTime',
			width: 265,
			type: 'dateTime',
			isLocal: true,
			filterType: 'dateTime',
			showSecond: true,
			isNotformfield: true
		},
		{
			header: 'TagID',
			field: 'code',
			headerFilter: true,
			standaloneHeaderFilter: true,
			hide: true,
			eager: true,
			isNotformfield: true
		},
		{
			field: 'keywordSearch',
			header: 'KeywordSearch',
			headerFilter: true,
			standaloneHeaderFilter: true,
			hide: true,
			eager: true,
			isNotformfield: true
		},
		{
			field: 'Delete',
			header: 'Delete',
			width: 150,
			type: 'action',
			isNotformfield: true,
			action: 'delete',

			renderCell: (params) => (
				<ActionIconButton
					onClick={() => params.colDef.deleteRow(params)}
					color="error"
					variant="contained"
					title={strings.Delete}
					size="small"
					disabled={params.row?.interfacedYN || consts.RoleCheck.includes(util?.getLoginData()?.roles[0])}
				>
					<DeleteIcon fontSize="inherit" />
				</ActionIconButton>
			)
		}
	]
});

export default model.createFormBase();
